import type {
  WidgetData,
  WidgetDataKind,
  WidgetDeclaration,
  WidgetSeriesRow,
} from '@crowdex/plugin-contracts';
import { BarChart, LineChart, RecordsList, RecordsTable, Stat } from './charts';
import type { ValueFormat } from './format';

/** How a placed widget reads: from its declaration, overridden where it is placed. */
export type WidgetPresentation = Pick<WidgetDeclaration, 'format' | 'currency' | 'better' | 'link'>;

/**
 * Where a widget's data stands: fetching, shown, waiting for a service the user has not set up
 * (said quietly, never as a failure), or failed.
 */
export type WidgetState =
  | { status: 'loading' }
  | { status: 'ready'; data: WidgetData; fetchedAt?: string }
  | { status: 'waiting'; service: string; name: string }
  | { status: 'failed'; error: string };

/** A data widget's data, drawn as its kind says. */
export function WidgetBody({
  kind,
  data,
  presentation = {},
  range,
}: {
  kind: WidgetDataKind;
  data: WidgetData;
  presentation?: WidgetPresentation;
  /** The range its stat compares with the one before (`30d`). */
  range?: string;
}) {
  const format: ValueFormat = {
    format: presentation.format,
    currency:
      data.kind === 'series' ? (data.currency ?? presentation.currency) : presentation.currency,
  };
  if (data.kind === 'records') {
    if (data.records.length === 0) return <p className="widget-placeholder">Nothing to show</p>;
    return kind === 'list' ? (
      <RecordsList
        columns={data.columns}
        format={format}
        link={presentation.link}
        records={data.records}
      />
    ) : (
      <RecordsTable
        columns={data.columns}
        format={format}
        link={presentation.link}
        records={data.records}
      />
    );
  }
  if (kind === 'stat')
    return (
      <Stat
        better={presentation.better}
        format={format}
        previous={data.previous}
        range={range}
        rows={data.rows}
        trend={data.trend}
      />
    );
  if (data.rows.length === 0) return <p className="widget-placeholder">Nothing to show</p>;
  if (kind === 'line') return <LineChart format={format} rows={data.rows} />;
  if (kind === 'bar') return <BarChart format={format} rows={data.rows} />;
  // A table or a list of a series: its rows as records.
  const records = data.rows.map((row: WidgetSeriesRow) => ({
    ...(row.at === undefined ? {} : { period: row.at.slice(0, 10) }),
    ...(row.group === undefined ? {} : { group: row.group }),
    value: row.value,
  }));
  return (
    <RecordsTable
      columns={Object.keys(records[0] ?? { value: 0 })}
      format={format}
      records={records}
    />
  );
}

/** A data widget wherever it is: loading, drawn, waiting for a service or failed. */
export function WidgetContent({
  kind,
  state,
  presentation,
  range,
}: {
  kind: WidgetDataKind;
  state: WidgetState;
  presentation?: WidgetPresentation;
  range?: string;
}) {
  switch (state.status) {
    case 'loading':
      return (
        <div
          aria-busy="true"
          className="widget-skeleton"
          data-kind={kind === 'stat' ? 'stat' : 'chart'}
          role="status"
        />
      );
    case 'waiting':
      return <p className="widget-waiting">Waiting for {state.name}</p>;
    case 'failed':
      return (
        <p className="widget-error" role="alert">
          {state.error}
        </p>
      );
    case 'ready':
      return <WidgetBody data={state.data} kind={kind} presentation={presentation} range={range} />;
  }
}
